// ═══════════════════════════════════════════════════════════════
// OcEscopoControles.jsx — os controles que as DUAS abas compartilham
//
// POR QUE UM ARQUIVO SÓ, E NÃO UMA CÓPIA EM CADA TELA
//
// "Movimentação de risco" e "Triagem" mostram o mesmo universo com perguntas
// diferentes. Se o controle de escopo for escrito duas vezes, um dia uma das
// duas vai ganhar um caso novo e a outra não — e aí o cliente vê 20 companhias
// numa aba e 393 na outra, no mesmo minuto, com o mesmo login. Duas telas com
// contagens diferentes na frente do mesmo cliente destroem confiança mais
// rápido do que qualquer defeito.
//
// Esta base já pagou por esse erro de classe quatro vezes (o POST /users que
// esqueceu a checagem do /invite, o conceder que esqueceu o teto do PATCH, o
// revogar que escrevia oc_plan por fora da porta única, o percentil calculado
// contra os pares da corrida). Uma regra, um dono.
//
// ──────────────────────────────────────────────────────────────
// TRÊS RECUSAS PRÓPRIAS DESTE ARQUIVO
//
// 1. NUNCA mostrar o universo sem DIZER que é o universo. O servidor falha
//    aberto de propósito quando não consegue ler a carteira (é restrição
//    comercial sobre dado público da CVM, não fronteira de dado de terceiro —
//    derrubar todas as telas por um timeout seria pior). Mas falhar aberto em
//    silêncio seria outra coisa: `escopo.motivo` vira uma frase na tela.
//
// 2. NUNCA inventar o número de vagas livres. A resposta diz quantas o cliente
//    ESCOLHEU; não diz o tamanho do pacote. "Você tem 20 vagas livres" seria um
//    número plausível e não medido — a assinatura dos achados desta base.
//    O gancho comercial é escrito sem número.
//
// 3. NUNCA deixar "—" fazer o trabalho de "não é sua". Travessão lê como dado
//    faltando. A companhia fora do pacote sai com a direção do movimento e uma
//    frase que diz por que o número não está ali.
// ═══════════════════════════════════════════════════════════════

import React from 'react';

const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', roxo: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

export const fmtDataBase = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
};

const caixa = {
  background: C.paper, border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.amber}`,
  borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: C.ink,
  lineHeight: 1.55, marginBottom: 12,
};

const pilula = (ativo) => ({
  padding: '5px 13px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
  border: `1px solid ${ativo ? C.navy : C.line}`,
  background: ativo ? C.navy : C.paper,
  color: ativo ? '#fff' : C.ink,
  fontWeight: ativo ? 600 : 400,
});

const sel = {
  padding: '5px 8px', border: `1px solid ${C.line}`, borderRadius: 8,
  fontSize: 12.5, color: C.ink, background: C.paper, outline: 'none',
};

/* ─────────────────────────────────────────────────────── escopo por pacote ─ */

/**
 * O seletor carteira/universo, mais o denominador honesto.
 *
 * `esc` é `d.escopo`, montado em services/oc/oc_carteira_escopo.js +
 * oc_deterioracao.js. Três estados, e os três levam a telas diferentes:
 *
 *   aplicado: true                  -> o caso normal, com os dois botões
 *   aplicado: false, motivo sem_*   -> uso interno / sem pacote: sem botão,
 *                                      porque não há o que escolher
 *   aplicado: false, motivo erro:*  -> RECUSA 1: mostra o universo E DIZ
 */
export function ControleEscopo({ esc, modo, onModo, carregando }) {
  if (!esc) return null;

  if (!esc.aplicado) {
    const falhou = String(esc.motivo || '').startsWith('erro:');
    if (!falhou) {
      /* Sem tenant (acesso interno) ou sem ciclo (cliente ainda sem pacote).
         Não há escolha a oferecer; dizer qual dos dois é ajuda o suporte. */
      return (
        <div style={{ ...caixa, borderLeftColor: C.muted }}>
          Esta tela está mostrando o <b>universo inteiro</b> do Capital Aberto.
          {esc.motivo === 'sem_ciclo' && <> Nenhum pacote de companhias foi montado para este cliente ainda.</>}
          {esc.motivo === 'sem_tenant' && <> Acesso interno: sem pacote associado.</>}
        </div>
      );
    }
    /* RECUSA 1. O servidor seguiu aberto de propósito; a tela não pode deixar
       isso passar como se fosse o estado normal. */
    return (
      <div style={{ ...caixa, borderLeftColor: C.red }}>
        <b>Não consegui ler a sua carteira agora, então esta tela está mostrando o universo inteiro.</b>{' '}
        As companhias abaixo <i>não</i> são necessariamente as do seu pacote. Os números de PD e
        variação estão corretos; o que está errado é o recorte.
        <div style={{ color: C.muted, marginTop: 4, fontSize: 12 }}>Motivo: {esc.motivo}</div>
      </div>
    );
  }

  const naCarteira = modo === 'carteira';
  const faltando = esc.n_ausentes || 0;

  return (
    <div style={{ ...caixa, borderLeftColor: C.gold }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, marginRight: 4 }}>O que aparece nesta tela:</span>
        <button style={pilula(naCarteira)} onClick={() => onModo('carteira')} disabled={carregando}>
          Minha carteira
        </button>
        <button style={pilula(!naCarteira)} onClick={() => onModo('universo')} disabled={carregando}>
          Todo o universo
        </button>
        {carregando && <span style={{ fontSize: 12, color: C.muted }}>consultando…</span>}
      </div>

      {/* RECUSA 3 do backend, do lado da tela: escolhidas, lidas e ausentes são
          três números diferentes, e "3 das suas 20" quando o motor leu 18 é uma
          mentira pequena que vira uma reunião ruim. */}
      <div style={{ color: C.muted, marginTop: 7 }}>
        Você tem <b>{esc.n_selecionadas}</b> companhia{esc.n_selecionadas === 1 ? '' : 's'} no seu pacote.
        {faltando > 0 ? (
          /* Concordância verbal: o sujeito é "a outra"/"as outras N". Escrever
             "as outras 4 ainda não publicou" foi o que apareceu em produção no
             primeiro minuto — erro pequeno, mas numa tela que o cliente lê para
             decidir corte de limite ele custa mais do que parece. */
          <> Nesta data-base há leitura de <b>{esc.n_nesta_data_base}</b> delas
            {faltando === 1
              ? <> — a outra ainda não publicou balanço nesta safra, então ela não entra nas contas abaixo.</>
              : <> — as outras {faltando} ainda não publicaram balanço nesta safra, então elas não entram nas contas abaixo.</>}
          </>
        ) : (
          <> Todas têm leitura nesta data-base.</>
        )}
      </div>

      {!naCarteira && (
        /* RECUSA 2: o gancho comercial, sem número inventado. */
        <div style={{ color: C.muted, marginTop: 6 }}>
          No modo <b>universo</b> aparecem também as companhias fora do seu pacote — com nome,
          setor, rating e a direção do movimento, mas sem a leitura de PD.
          {esc.n_fora_da_carteira_nesta_tela > 0 && (
            <> São <b>{esc.n_fora_da_carteira_nesta_tela}</b> nesta data-base.</>
          )}{' '}
          A leitura de uma delas abre quando ela entra na sua carteira.
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────── seleção de trimestre ─ */

/**
 * Os DOIS trimestres comparados, os dois escolhidos à mão.
 *
 * A primeira versão abria só o lado direito, e o Alexandre apontou na hora:
 * "seleção comparativa de trimestres" são dois seletores. Com um só, dá para
 * perguntar "como 30/06 se compara com 31/03?" mas não "como estava o mundo em
 * 31/03 contra 31/12?" — que é a pergunta de quem quer entender a série, não só
 * a última leitura. O backend já aceitava os dois (`data_base` e
 * `data_base_comparacao`); faltava a tela.
 *
 * A decisão que este componente TEM de tornar visível: quando o usuário escolhe,
 * o piso de população sai de cena no servidor. Comparar 393 contra 112 produz um
 * painel plausível e errado — foi o caso real de 22/08/2026 — e quem escolheu
 * precisa ver isso ao lado da própria escolha, não descobrir depois.
 */
/**
 * Quem desenha o cabeçalho: o seletor, ou a caixa antiga?
 *
 * EXATAMENTE UM DOS DOIS, sempre. O seletor absorveu o conteúdo do cabeçalho
 * (intervalo em dias, recusa do "MoM", denominador), então deixar os dois
 * ligados repete o par de datas — foi o que o cliente viu em produção. E
 * desligar o antigo sem condição apagaria o cabeçalho inteiro contra um backend
 * que não manda `safras_disponiveis`, que é o estado de qualquer ambiente ainda
 * não atualizado.
 *
 * A regra mora aqui, com o componente que a torna verdadeira, e não repetida
 * nas duas telas.
 */
export function temSeletorDeTrimestre(d) {
  return (d && Array.isArray(d.safras_disponiveis) && d.safras_disponiveis.length >= 2) || false;
}

export function SeletorTrimestres({ d, dataBase, onDataBase, comparacao, onComparacao, carregando }) {
  const safras = d.safras_disponiveis || [];
  if (!temSeletorDeTrimestre(d)) return null;

  const corrente = safras.find((s) => s.data_base === d.data_base);
  const anteriores = safras.filter((s) => s.data_base < d.data_base);
  const escolhida = safras.find((s) => s.data_base === d.data_base_comparacao);

  /* A SAFRA CORRENTE NÃO TEM PISO, E ISSO APARECE TODO TRIMESTRE.
     ──────────────────────────────────────────────────────────────
     `escolherSafras` faz `const corrente = lista[0]` sem checar população: o
     piso só protege o lado da comparação. Consequência, perguntada pelo
     Alexandre em 18/09/2026: no dia em que as três primeiras companhias
     publicarem o balanço de 30/09, a tela anuncia "Posição de 30/09/2026" com
     três nomes, e a carteira de 20 aparece com um ou dois. Não quebra nada —
     fica errada por algumas semanas, todo trimestre, até a safra encher.

     A correção NÃO é travar a virada: dado novo é dado novo, e esconder a safra
     nova seria a tela decidir o que o cliente pode ver. A correção é dizer que
     ela está enchendo e deixar a anterior a um clique.

     O limiar é o `piso_de_populacao_pct` do servidor — o mesmo número, não um
     segundo critério escrito aqui. Piso que muda no motor e não muda na tela
     vira mentira silenciosa. */
  const anteriorMaisRecente = anteriores[0] || null;
  const pisoPctServidor = d.piso_de_populacao_pct;
  const enchendo = !!(corrente && anteriorMaisRecente && pisoPctServidor != null
    && corrente.n < Math.ceil(anteriorMaisRecente.n * (pisoPctServidor / 100)));

  /* O piso vem do servidor (`piso_de_populacao_pct`), não escrito à mão aqui:
     mudar o piso no motor e não mudar na tela vira mentira silenciosa — foi
     assim que "58,96%" chegou a produção na PR #10. */
  const pisoPct = d.piso_de_populacao_pct;
  const piso = (corrente && pisoPct != null) ? Math.ceil(corrente.n * (pisoPct / 100)) : null;
  const rala = !!(escolhida && piso != null && escolhida.n < piso);

  /* Oito trimestres = dois anos. O corte é de LEGIBILIDADE, não de verdade: as
     antigas continuam na lista, no grupo de baixo. Uma escolhida fora dos oito
     sobe para o grupo de cima, senão o `select` mostraria um valor selecionado
     escondido atrás de um rótulo que diz "mais antigos". */
  const RECENTES = 8;
  const recentes = anteriores.slice(0, RECENTES)
    .concat(anteriores.slice(RECENTES).filter((s) => s.data_base === d.data_base_comparacao));
  const antigas = anteriores.slice(RECENTES).filter((s) => s.data_base !== d.data_base_comparacao);

  return (
    <div style={{ ...caixa, borderLeftColor: rala ? C.amber2 : C.blue }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 700 }}>Comparar</span>
        {/* O LADO DE CIMA TAMBÉM ABRE. Trocar a data-base corrente derruba a
            comparação escolhida: guardar "31/03/2026" como comparação enquanto
            a corrente vira "31/12/2025" pede ao servidor um delta para a frente,
            que ele recusa com 409 — e o cliente veria um erro por causa de um
            estado que a tela deixou inconsistente sozinha. Volta para
            automático, que é sempre válido. */}
        <select
          style={sel}
          value={dataBase || d.data_base}
          disabled={carregando}
          onChange={(e) => {
            const nova = e.target.value;
            if (comparacao && comparacao >= nova) onComparacao(null);
            onDataBase(nova === d.safras_disponiveis[0].data_base ? null : nova);
          }}
        >
          {safras.map((s) => (
            <option key={s.data_base} value={s.data_base}>
              {fmtDataBase(s.data_base)}{s.data_base === d.safras_disponiveis[0].data_base ? ' (mais recente)' : ''}
            </option>
          ))}
        </select>
        {/* O NÚMERO PRECISA DIZER DE QUEM ELE É.
            Em produção este "(393 companhias)" ficou logo abaixo de "você tem 20
            no seu pacote, 16 com leitura nesta data-base" — três números na mesma
            caixa, dois deles do cliente e um do acervo, e nada dizendo qual era
            qual. É a mesma regra do denominador de sempre: número sem dono lê
            como contradição. Aqui ele é a POPULAÇÃO DA BASE, e é ela que decide
            se um trimestre de comparação é raso — por isso ele existe. */}
        {corrente && (
          <span style={{ color: C.muted }} title="População da base inteira nesta data-base. É com ela que se julga se o trimestre de comparação é raso — não tem relação com o tamanho do seu pacote.">
            ({corrente.n} companhias na base)
          </span>
        )}
        <span>com</span>
        <select
          style={sel}
          value={d.data_base_comparacao || ''}
          disabled={carregando}
          onChange={(e) => onComparacao(e.target.value || null)}
        >
          <option value="">automático (a mais recente com população suficiente)</option>
          {/* A LISTA INTEIRA CONTINUA AQUI, SÓ QUE SEPARADA.
              O acervo tem safra desde 2020: sem separação, o seletor abre com 25
              opções e "30/09/2020" fica visualmente do mesmo tamanho que
              "31/03/2026". Delta entre 2026 e 2020 não é deterioração, é
              calendário — e essa é exatamente a confusão que a âncora desta tela
              existe para evitar.
              Esconder as antigas seria pior: alguém vai querer olhar dois anos
              atrás de propósito, e tela que some com dado obriga o cliente a
              pedir por e-mail. Então: agrupadas e rotuladas, não filtradas. */}
          {recentes.length > 0 && (
            <optgroup label="Trimestres recentes">
              {recentes.map((s) => (
                <option key={s.data_base} value={s.data_base}>
                  {fmtDataBase(s.data_base)} — {s.n} companhia{s.n === 1 ? '' : 's'}
                </option>
              ))}
            </optgroup>
          )}
          {antigas.length > 0 && (
            <optgroup label="Mais antigos — a diferença passa a ser de cobertura, não de risco">
              {antigas.map((s) => (
                <option key={s.data_base} value={s.data_base}>
                  {fmtDataBase(s.data_base)} — {s.n} companhia{s.n === 1 ? '' : 's'}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {carregando && <span style={{ fontSize: 12, color: C.muted }}>consultando…</span>}
      </div>

      {/* O QUE ERA A CAIXA "Posição de X · comparada com Y".
          ────────────────────────────────────────────────────
          Ela existia quando as datas não eram escolhíveis. Com os dois seletores
          acima, o título dela virava eco: duas caixas empilhadas dizendo o mesmo
          par de datas. O Alexandre apontou em 18/09/2026.

          Mas a caixa antiga carregava DUAS coisas que os seletores não dizem, e
          nenhuma das duas é decorativa:

            · o INTERVALO em dias e a recusa de chamar a variação de mensal — o
              cliente escreveu "+10 p.p. MoM" no e-mail que originou esta tela, e
              não é MoM: é entre duas data-base trimestrais;
            · o DENOMINADOR — "16 de 393" quer dizer algo, "16" não quer.

          Então o título saiu e o conteúdo desceu para cá. Uma caixa, nada
          repetido, nada perdido. */}
      {d.data_base_comparacao ? (
        <div style={{ color: C.muted, marginTop: 7 }}>
          A variação é entre estas duas data-base
          {d.dias_entre_bases != null && <> — <b>{d.dias_entre_bases} dias</b> de intervalo</>}, não entre
          meses. O RiskMind lê balanço trimestral: não existe leitura mensal.
          {d.motivo_comparacao && !d.comparacao_forcada && <> Base escolhida: {d.motivo_comparacao}.</>}
        </div>
      ) : (
        <div style={{ color: C.muted, marginTop: 7 }}>
          Sem safra anterior que atenda ao piso de população: os cortes de nível valem, o salto
          entre bases fica indisponível. {d.motivo_comparacao}
        </div>
      )}

      <div style={{ color: C.muted, marginTop: 4 }}>
        <b>{d.universo?.n ?? '—'}</b> companhias nesta tela
        {d.universo?.n_comparavel != null && <> · {d.universo.n_comparavel} com leitura anterior</>}
        {d.universo?.n_sem_base_anterior ? <> · {d.universo.n_sem_base_anterior} entraram agora</> : null}
      </div>

      {enchendo && (
        /* Ver o bloco `enchendo`, acima. Aviso, não bloqueio. */
        <div style={{ marginTop: 7 }}>
          <b>Esta data-base ainda está enchendo.</b>{' '}
          {corrente.n} companhias contra {anteriorMaisRecente.n} no trimestre anterior. As companhias
          publicam balanço em datas diferentes, e a maioria ainda não publicou o de{' '}
          {fmtDataBase(corrente.data_base)} — então a lista abaixo é uma amostra de quem publicou
          cedo, não um retrato do mercado.
          <div style={{ color: C.muted, marginTop: 4 }}>
            Para ver o trimestre fechado, escolha{' '}
            <button
              onClick={() => onDataBase(anteriorMaisRecente.data_base)}
              disabled={carregando}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.blue, fontWeight: 600, fontSize: 12.5, textDecoration: 'underline' }}
            >
              {fmtDataBase(anteriorMaisRecente.data_base)}
            </button>{' '}
            acima ({anteriorMaisRecente.n} companhias).
          </div>
        </div>
      )}

      {rala && (
        /* O aviso não corrige a escolha. Ele a informa. */
        <div style={{ marginTop: 7 }}>
          <b>Este trimestre tem população menor que a data-base atual.</b>{' '}
          {escolhida.n} companhias contra {corrente.n}. Boa parte do que vai aparecer como
          &ldquo;entrou agora&rdquo; ou como salto é diferença de <i>cobertura</i>, não de risco:
          a companhia não piorou, ela simplesmente não tinha leitura naquele trimestre.
          <div style={{ color: C.muted, marginTop: 4 }}>
            O automático evita isso pulando safras abaixo de {pisoPct}% da população atual
            ({piso} companhias). Você pediu esta, então ela está sendo usada.
          </div>
        </div>
      )}

      {!rala && d.comparacao_forcada && (
        <div style={{ color: C.muted, marginTop: 6 }}>
          Comparação escolhida por você. O automático teria usado o mesmo critério de população.
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────── as células de uma linha mascarada ─ */

/* "leitura idêntica" NÃO pode aparecer aqui, e isso não é preciosismo de texto.
   Nas linhas da carteira essa frase tem um sentido preciso e trabalhado: as duas
   leituras batem até a segunda casa, o que NÃO é estabilidade medida — é o
   ensemble não tendo tido como distinguir. Reusá-la na linha mascarada fundiria
   duas afirmações diferentes sob a mesma palavra, e a versão mascarada nem
   carrega a ressalva que dá sentido à outra. Aqui é só a direção: subiu, caiu,
   não mudou. */
const DIRECAO = {
  piorou: { texto: 'PD subiu', cor: C.red },
  melhorou: { texto: 'PD caiu', cor: C.green },
  estavel: { texto: 'PD estável', cor: C.muted },
};

/** O sinal, sem o número. É isto que a companhia fora do pacote entrega. */
export function Direcao({ linha }) {
  const d = DIRECAO[linha.direcao];
  if (!d) {
    return (
      <span style={{ color: C.muted, fontSize: 11.5 }}
        title="Sem leitura na data-base anterior, não há direção para comparar.">
        sem base anterior
      </span>
    );
  }
  return (
    <span style={{ color: d.cor, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
      title="A companhia não está no seu pacote: a direção do movimento aparece, a leitura de PD não.">
      {d.texto}
    </span>
  );
}

/**
 * RECUSA 3. Onde iria o número de uma companhia que não é do cliente.
 * Não é "—": travessão lê como dado faltando, e o dado não está faltando.
 */
export function ForaDoPacote({ curto }) {
  return (
    <span
      style={{ color: C.roxo, fontSize: curto ? 11 : 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}
      title="Esta companhia não está no seu pacote. O RiskMind calculou a PD dela, mas a leitura só abre para as companhias da sua carteira."
    >
      fora do pacote
    </span>
  );
}

export default { ControleEscopo, SeletorTrimestres, temSeletorDeTrimestre, Direcao, ForaDoPacote, fmtDataBase };
